<template>
  <div id="proposals_list">

    <b-row>
      <b-col cols="2" offset="10">
        <b-form-group>
          <div class="right div--button w-100">
            <span>
              Filtered rows: {{ proposalsLength }}
            </span>
          </div>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="2">
        <label class="font-weight-bold mt-3" for="timeFilterType">Filter proposals by:</label>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="2">
        <b-form-group>
          <b-form-radio-group id="timeFilterType"
                              v-model="timeFilterType"
                              :options="timeFilterTypeOptions"
                              button-variant="outline-secondary"
                              buttons
                              class="w-100 checkbox-group--rows"
                              stacked/>
        </b-form-group>
      </b-col>
      <b-col cols="4">
        <b-row v-if="isCallFilter"
               align-v="center"
               class="mb-3">
          <b-col cols="6"
                 md="auto">
            Filter proposals by call:
          </b-col>
          <b-col cols="6"
                 md="auto">
            <b-form-select v-model="selectedCall"
                           :options="callOptions"
                           @change="chooseCall"/>
          </b-col>
        </b-row>
        <b-input-group v-else
                       class="mb-3"
                       prepend="Filter proposals (from - to):">
          <b-form-input v-model="query.from"
                        :class="{'custom-invalid-input-border custom-input-border-radius': $v.query.from.$error}"
                        aria-label="From"
                        placeholder="YYYY-MM-DD"
                        @input="$v.query.from.$touch()"/>
          <b-form-input v-model="query.to"
                        :class="{'custom-invalid-input-border custom-input-border-radius': $v.query.to.$error}"
                        aria-label="To"
                        placeholder="YYYY-MM-DD"
                        @input="$v.query.to.$touch()"/>
        </b-input-group>

      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <p class="mb-4 grey--text">
          Draft and Rapid Access proposals can only be filtered by a date range, while all other proposal types can only
          be filtered by call.
        </p>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="2">
        <label class="font-weight-bold mt-3" for="infrastructures">Proposal infrastructures:</label>
      </b-col>
      <b-col cols="2">
        <label class="font-weight-bold mt-3" for="states">Proposal states:</label>
      </b-col>
      <b-col cols="2">
        <label class="font-weight-bold mt-3" for="grades">Proposal grades:</label>
      </b-col>
      <b-col cols="4">
        <label class="font-weight-bold mt-3" for="types">Proposal types:</label>
      </b-col>
      <b-col cols="2">
        <label class="font-weight-bold mt-3" for="ceric">Display CERIC proposals:</label>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="4">
        <b-row>
          <b-col cols="6">
            <b-form-group>
              <b-form-checkbox-group id="infrastructures"
                                     v-model="query.infrastructures"
                                     :options="proposalOptions.infrastructures"
                                     button-variant="outline-secondary"
                                     buttons
                                     class="w-100 checkbox-group--rows"
                                     stacked/>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group>
              <b-form-checkbox-group id="states"
                                     v-model="query.proposal_states"
                                     :options="proposalOptions.states"
                                     button-variant="outline-secondary"
                                     buttons
                                     class="w-100 checkbox-group--rows"
                                     stacked/>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <p class="mb-4 grey--text">For CERIC proposals, states indicate whether the proposal has been submitted by
              the User Office.</p>
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="2">
        <b-form-group>
          <b-form-checkbox-group id="grades"
                                 v-model="query.proposal_grades"
                                 :options="proposalOptions.grades"
                                 button-variant="outline-secondary"
                                 buttons
                                 class="w-100 checkbox-group--rows"
                                 stacked/>
        </b-form-group>
      </b-col>
      <b-col cols="4">
        <b-form-group>
          <b-form-checkbox-group id="types"
                                 v-model="query.proposal_types"
                                 :options="proposalOptions.types"
                                 button-variant="outline-secondary"
                                 buttons
                                 class="w-100 checkbox-group--rows"
                                 stacked/>
        </b-form-group>
      </b-col>
      <b-col cols="2">
        <b-form-group>
          <b-form-radio-group id="ceric"
                              v-model="displayCeric"
                              :options="cericOptions"
                              button-variant="outline-secondary"
                              buttons
                              class="w-100 checkbox-group--rows"
                              stacked/>
        </b-form-group>
        <b-button class="w-100"
                  variant="info"
                  @click="openModalGroupMails">
          Send group mails
        </b-button>
        <basic-mailer-form ref="mailerFormModal"
                           :filter-query="this.filterQuery"
                           :is-proposals-submitted-state="this.isProposalStateChosen('submitted')"
                           :mail-content="this.mailContent"
                           :users-mailing-list="this.usersMailingList"/>
      </b-col>
    </b-row>

    <b-button id="loadProposals"
              class="w-100 mt-2 mb-2"
              variant="info"
              @click="loadData">
      Load proposals
    </b-button>

    <b-table :current-page="currentPage"
             :fields="fields"
             :items="workingProposals"
             :per-page="perPage"
             head-variant="light"
             hover
             outlined
             stacked="lg"
             striped>

      <template v-slot:top-row="{fields}">
        <proposal-filter :id="id"
                         v-model="proposals"
                         :fields="fields"
                         :not-include-key="['actions', 'ordinalNumber']"
                         @input="clearWorkingProposals"/>
      </template>

      <template v-slot:cell(ordinalNumber)="row">
        <base-column-ordinal-number v-bind="{index: row.index, perPage, currentPage}"/>
      </template>

      <template v-slot:cell(states.name)="row">
        <div v-if="row.item.document_specification.subtype">
          <span v-text="row.item.document_specification.subtype.ceric.states.name"/>
        </div>
        <div v-else v-text="row.item.states.name"></div>
      </template>

      <template v-slot:cell(document_specification.subtype)="row">
        <div v-if="row.item.document_specification.subtypes">
          <span v-text="row.item.document_specification.subtypes"/>
        </div>
      </template>

      <template v-slot:cell(states.date)="row">
        <div v-if="row.item.document_specification.subtype">
          <span v-text="row.item.document_specification.subtype.ceric.states.date"/>
        </div>
        <div v-else v-text="row.item.states.date"></div>
      </template>

      <template v-slot:cell(actions)="row">
        <action-open-proposal-full-details :proposal="row.item"/>

        <base-action-expand-details v-if="row.item.team.team_members.length > 1"
                                    :b-table-row="row"/>
      </template>

      <template v-slot:row-details="row">
        <team-form :editable="editable"
                   :proposal-object-id="getProposalObjectId(row)"
                   :selector-on="selectorOn"
                   old-cryoem-team-roles-mapping>
          <template #header>
            <span>  </span>
          </template>
        </team-form>
      </template>

    </b-table>

    <b-table-footer v-model="isBusy"
                    :current-page="currentPage"
                    :data-length="proposalsLength"
                    :per-page="perPage"
                    @change-page="changePage">
      <template v-slot>
        <base-per-page-input v-model="perPage"/>
      </template>
    </b-table-footer>
  </div>
</template>

<script>
  import { dateRegex } from '@/assets/validators/regexValidator';
  import ActionOpenProposalFullDetails from '@/components/actions/ActionOpenProposalFullDetails';
  import BaseActionExpandDetails from '@/components/actions/BaseActionExpandDetails';
  import BaseColumnOrdinalNumber from '@/components/base-components/BaseColumnOrdinalNumber';
  import BasePerPageInput from '@/components/base-components/BasePerPageInput';
  import BasicMailerForm from '@/components/mailer/BasicMailerForm';
  import BTableFooter from '@/components/BTableFooter';
  import ProposalFilter from '@/components/ProposalFilter';
  import TeamForm from '@/components/TeamForm';
  import { infrastructureMixin } from '@/mixins/infrastructure';
  import moment from 'moment/moment';
  import { mapState } from 'vuex';
  import Vue from 'vue';

  export default {
    name: 'ProposalsList',
    components: {
      ActionOpenProposalFullDetails,
      BaseActionExpandDetails,
      BaseColumnOrdinalNumber,
      BasePerPageInput,
      BasicMailerForm,
      BTableFooter,
      ProposalFilter,
      TeamForm,
    },
    mixins: [infrastructureMixin],
    props: {
      fields: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        selectorOn: false,
        editable: false,
        selectedCall: 'None',
        isBusy: false,
        proposals: [],
        workingProposals: [],
        currentPage: 1,
        perPage: 20,
        id: 1,
        displayCeric: true,
        query: {
          call_name: 'None',
          proposal_states: [],
          proposal_types: [],
          subtypes: ['ceric'],
        },
        teamComponentName: '',
        timeFilterType: 'call',
        timeFilterTypeOptions: [
          { text: 'Call', value: 'call' },
          { text: 'Date range', value: 'date' },
        ],
        mailContent: '',
        filterQuery: {},
        usersMailingList: [],
      };
    },
    computed: {
      callOptions() {
        const callNames = this.calls.map((call) => call.name);
        return [...callNames, 'None'];
      },
      cericOptions() {
        return [
          {
            text: 'Yes',
            value: true,
            buttonVariant: 'outline-info',
            disabled: !this.canUseCallBasedTypes || this.hasQuerySubmittedInDateFilter,
          },
          {
            text: 'No',
            value: false,
            buttonVariant: 'outline-info',
          },
        ];
      },
      canUseGrades() {
        return this.isCallFilter || this.hasQueryRapidAccess;
      },
      canUseCallBasedTypes() {
        return this.isCallFilter || this.hasQueryDraft;
      },
      hasQueryRapidAccess() {
        return !!this.query?.proposal_types?.includes('Rapid access');
      },
      hasQueryDraft() {
        return !!this.query?.proposal_states?.includes('draft');
      },
      hasQuerySubmittedInDateFilter() {
        return !!this.query?.proposal_states?.includes('submitted') && this.isDateFilter;
      },
      isCallFilter() {
        return this.timeFilterType === 'call';
      },
      isDateFilter() {
        return this.timeFilterType === 'date';
      },
      ...mapState('calls', ['calls']),
      proposalOptions() {
        return {
          infrastructures: [
            { text: 'Synchrotron', value: 'synchrotron' },
            { text: 'CryoEm', value: 'cryoem' },
          ],
          states: [
            { text: 'Draft', value: 'draft', disabled: this.isCallFilter },
            { text: 'Submitted', value: 'submitted' },
          ],
          grades: [
            { text: 'Approved', value: 'approved', disabled: !this.canUseGrades || this.hasQueryDraft },
            { text: 'Rejected', value: 'rejected', disabled: !this.canUseGrades || this.hasQueryDraft },
            { text: 'Waiting list', value: 'reserve', disabled: !this.canUseGrades || this.hasQueryDraft },
            { text: 'No evaluation yet', value: 'none', disabled: !this.canUseGrades || this.hasQueryDraft },
          ],
          types: [
            {
              text: 'Standard',
              value: 'Standard',
              disabled: !this.canUseCallBasedTypes || this.hasQuerySubmittedInDateFilter,
            },
            { text: 'Rapid access', value: 'Rapid access', disabled: this.isCallFilter },
            {
              text: 'Long term',
              value: 'Long term',
              disabled: !this.canUseCallBasedTypes || this.hasQuerySubmittedInDateFilter,
            },
          ],
        };
      },
      proposalsLength() {
        return this.proposals.length;
      },
    },
    watch: {
      timeFilterType: {
        handler() {
          this.query = {
            from: '',
            proposal_states: [],
            proposal_types: [],
            proposal_grades: [],
            subtypes: [],
            to: '',
          };
          if (this.isCallFilter) {
            this.selectedCall = this.callOptions[this.callOptions.length - 1];
            this.chooseCall();
          } else {
            this.setDefaultDates();
            this.selectedCall = this.callOptions[this.callOptions.length - 1];
            this.displayCeric = false;
          }
        },
      },
      displayCeric: {
        handler() {
          if (this.displayCeric) {
            this.query.subtypes = ['ceric'];
          } else {
            this.query.subtypes = [];
          }
        },
      },
      'query.proposal_states': {
        deep: true,
        handler() {
          if (this.isDateFilter) {
            if (this.query?.proposal_states?.includes('draft')) {
              this.deleteGradesFromQuery();
            }
            if (this.query?.proposal_states?.includes('submitted')) {
              this.deleteSomeTypesFromQuery();
              this.displayCeric = false;
            }
            if (!this.query?.proposal_states?.includes('submitted')) {
              this.deleteGradesFromQuery();
            }
          }
        },
      },
    },
    created() {
      this.loadCalls();
    },
    methods: {
      addEndStationToCryoemProposals() {
        this.proposals = this.proposals.map((proposal) => {
          if (this.isProposalOfInfrastructureType(proposal, 'cryoem')) {
            // eslint-disable-next-line camelcase
            const general = { ...proposal.general, end_station: 'CryoEm' };
            return { ...proposal, general };
          }
          return proposal;
        });
      },
      changePage(val) {
        this.currentPage = val;
        this.clearWorkingProposals();
      },
      deleteGradesFromQuery() {
        this.$set(this.query, 'proposal_grades', []);
      },
      deleteSomeTypesFromQuery() {
        this.query.proposal_types = this.query?.proposal_types?.filter(
          (item) => !['Standard', 'Long term'].includes(item),
        );
      },
      chooseCall() {
        this.$set(this.query, 'call_name', this.selectedCall);
      },
      clearWorkingProposals() {
        this.$set(this, 'workingProposals', JSON.parse(JSON.stringify(this.proposals)));
      },
      concatenateProposalSubtypes() {
        this.proposals.forEach((el) => {
          const { document_specification } = el;
          // eslint-disable-next-line camelcase
          const { subtype } = document_specification;
          if (subtype) {
            // eslint-disable-next-line camelcase
            document_specification.subtypes = Object.keys(subtype).join(', ');
          }
        });
      },
      async getMailContent() {
        this.axios.get('/mails/proposals-grades', { params: this.query }).then((response) => {
          this.mailContent = response.data.mail_content;
          this.filterQuery = this.query;
          this.usersMailingList = response.data.users_mailing_list;
        }).catch(() => {
          const notification = { title: 'There is no valid mails to send', type: 'error' };
          Vue.notify(notification);
        });
      },
      getProposalObjectId(row) {
        return this.getId(row.item);
      },
      isInfrastructuresChosenToSendGroupMails() {
        if (this.query.infrastructures) {
          return this.query.infrastructures.length === 1;
        }
        return false;
      },
      isProposalStateChosen(state) {
        if (this.query.proposal_states) {
          return (this.query.proposal_states.includes(state) && (this.query.proposal_states.length === 1));
        }
        return false;
      },
      isProposalsGradesChosenToSendGroupMails() {
        if ('proposal_grades' in this.query) {
          return this.query.proposal_grades.length === 1;
        }
        return false;
      },
      isProposalsTypesChosenToSendGroupMails() {
        if (this.query.infrastructures.includes('cryoem')) {
          return true;
        }
        if ('proposal_types' in this.query) {
          return this.query.proposal_types.length === 1;
        }
        return false;
      },
      async loadCalls() {
        await this.$store.dispatch('calls/load_calls');
        this.selectedCall = this.callOptions[this.callOptions.length - 1];
      },
      setDefaultDates() {
        this.$set(this.query, 'from', '2018-01-01');
        this.$set(this.query, 'to', moment().format('YYYY-MM-DD'));
      },
      validateFilters() {
        this.updateQueryWithDatesIfCallNone();
        let requiredFilters = ['infrastructures', 'proposal_states', 'proposal_types'];
        if (!this.hasQueryDraft) {
          requiredFilters = [...requiredFilters, 'proposal_grades'];
        }
        const missingFilter = requiredFilters.some((key) => (
          !this.query[key]
          || !Array.isArray(this.query[key])
          || this.query[key].length === 0
        ));
        if (missingFilter) {
          Vue.notify({ title: 'Please select at least one filter from each category.', type: 'error' });
          return false;
        }
        return true;
      },
      async loadData() {
        this.toggleBusy();
        const areFiltersValid = this.validateFilters();
        if (areFiltersValid) {
          const response = await this.axios.get('/documents', { params: this.query });
          this.$store.commit('login/proposals_query', this.query);
          this.proposals = this.mapReserveProposals(response.data);
          this.concatenateProposalSubtypes();
          this.addEndStationToCryoemProposals();
          this.clearWorkingProposals();
          this.$emit('setup-proposal', this.proposals);
          this.toggleBusy();
          await this.getMailContent();
        } else {
          this.toggleBusy();
        }
      },
      mapReserveProposals(proposals) {
        return proposals.map((proposal) => ({
          ...proposal,
          // eslint-disable-next-line camelcase
          acceptance_mark: proposal.acceptance_mark === 'reserve' ? 'waiting list' : proposal.acceptance_mark,
        }));
      },
      updateQueryWithDatesIfCallNone() {
        if (this.isCallFilter && this.selectedCall === 'None') {
          this.setDefaultDates();
        }
      },
      async openModalGroupMails() {
        let notification = {};
        if (this.query?.proposal_types?.includes('Rapid access')) {
          notification = { title: 'You cannot send Rapid access group emails', type: 'error' };
          Vue.notify(notification);
          return;
        }
        if (!(this.isInfrastructuresChosenToSendGroupMails()
          && this.isProposalsGradesChosenToSendGroupMails()
          && this.isProposalsTypesChosenToSendGroupMails()
          && this.isProposalStateChosen('submitted')
          && this.selectedCall !== 'None')) {
          if (!this.isProposalStateChosen('draft')) {
            notification = {
              title: 'Please select a call, the submitted state and '
                + 'a single infrastructure, grade and type before sending emails',
              type: 'error',
            };
            Vue.notify(notification);
            return;
          }
        }
        if (this.proposals.length === 0) {
          notification = { title: 'Without any loaded proposals, you cannot send group emails', type: 'error' };
          Vue.notify(notification);
        } else {
          await this.getMailContent();
          this.$refs.mailerFormModal.openModal();
        }
      },
      toggleBusy() {
        this.isBusy = !this.isBusy;
      },
    },
    validations: {
      query: {
        from: {
          isValid(date) {
            if (date !== '') {
              return !!(dateRegex.regex.test(date));
            }
            return true;
          },
        },
        to: {
          isValid(date) {
            if (date !== '') {
              return !!(dateRegex.regex.test(date));
            }
            return true;
          },
        },
      },
    },
  };
</script>

<style scoped>
  .checkbox-group--rows {
    .btn {
      font-weight: 600;
    }

    .btn.disabled,
    .btn:disabled {
      font-weight: 400;
      opacity: 0.5;
    }
  }
</style>
